<template>
	<div class="buildings-storage-box-wrapper">
		<div
			v-if="
				typeof storedBuildingsCount === 'string' ||
				storedBuildingsCount > 0
			"
			class="buildings-storage-box"
			@click="openStoragePopup"
		>
			<div class="buildings-storage-notification-wrapper">
				<AppNotification
					class="buildings-storage-notification"
					:value="storedBuildingsCount"
				/>
			</div>
			<div class="icon-storage-22" />
		</div>
	</div>
</template>

<script lang="ts" setup>
import AppNotification from "@/components/AppNotification.vue";
import StoragePopup from "@/components/StoragePopup.vue";
import { isTemporaryEventBuildingActive } from "@/event";
import {
	tilerepoRemovableEventBuildingsFrameNames,
	type FrameName,
} from "@/game/entities/config";
import useBuildingsStorageStore from "@/store/useBuildingsStorageStore";
import {
	PopupDictionary,
	PopupType,
	usePopupStore,
} from "@/store/usePopupStore";
import { SkinTypes } from "@/types/ui";
import { translate } from "@/utils/translate";
import { computed, markRaw } from "vue";

const buildingsStorageStore = useBuildingsStorageStore();
const popupStore = usePopupStore();

const openStoragePopup = (): void => {
	popupStore.showPopup({
		id: PopupDictionary.STORAGE,
		type: PopupType.INFO,
		heading: translate("upgrade.inventory"),
		skin: SkinTypes.DARK,
		height: 30,
		width: 24,
		isPaddingless: true,
		component: markRaw(StoragePopup),
	});
};

const storedBuildingsCount = computed((): string | number => {
	const storedBuildings = Object.entries(
		buildingsStorageStore.getAllBuildings(),
	)
		.filter(
			([frameName]: [string, number]): boolean =>
				!tilerepoRemovableEventBuildingsFrameNames.has(
					frameName as FrameName,
				) || isTemporaryEventBuildingActive(frameName as FrameName),
		)
		.filter(
			([, value]: [string, number]): boolean =>
				Number.isFinite(value) && value > 0,
		)
		.map(
			([frameName, value]: [string, number]): {
				frameName: string;
				value: number;
			} => ({
				frameName,
				value,
			}),
		)
		.reduce((acc, building): number => acc + building.value, 0);
	return storedBuildings >= 99 ? "99+" : storedBuildings;
});
</script>

<style scoped lang="scss">
.buildings-storage-box {
	display: flex;
	position: relative;
	justify-content: center;
	align-items: center;
	box-shadow: inset 0 0 0.5rem 0 rgba(0, 0, 0, 0.3);
	border-width: 0.075rem;
	border-style: solid;
	border-image-source: linear-gradient(to top, #bd9032, #f0d386);
	border-image-slice: 1;
	background-image: linear-gradient(to top, #181a1d, #303239);
	width: 100%;
	height: 100%;
	@include clickable-element;

	&-wrapper {
		margin-right: 0.5rem;
		width: 2.5rem;
		height: 2.5rem;
	}

	.buildings-storage-notification-wrapper {
		display: flex;
		position: absolute;
		top: -0.6975rem;
		left: 0;
		justify-content: center;
		align-items: center;
		width: 100%;
	}
}
</style>
